import { AccountContactInformationModal } from 'components/accounts/AccountContactInformationModal'
import {
  AccountContactMethodsDocument,
  AccountWithRelationshipsAndContactMethodsFragment,
  ContactMethodChannel,
  ContactMethodFragment,
  useAccountContactMethodsQuery,
  useDeleteContactMethodMutation,
} from 'graphql/bindings/AdminBindings'
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CheckIcon,
  CommentsIcon,
  EllipsisVIcon,
  EnvelopeIcon,
  GreyIconWrapper,
  InfoCardProps,
  Link,
  LinkedinIcon,
  MinusIcon,
  PencilAltIcon,
  PhoneIcon,
  SingleInfoCard,
  Theme,
  TrashIcon,
  Typography,
  WhatsappIcon,
  makeStyles,
  useModal,
} from '@forgeglobal/design-framework'
import { ChildAccountCard, ParentAccountCard } from 'components/accounts/AccountConnections'
import { PopoverAction, PopoverListItem, PopoverListItemIcon, PopoverListItemText } from 'isolated-design-library'
import { createValidAbsoluteLink } from 'util/Routes'
import React, { Fragment } from 'react'
import capitalize from 'lodash/capitalize'

export interface EmailPrefs {
  opt_in_general_marketing_emails?: boolean
  opt_in_opportunity_emails?: boolean
  opt_in_newsletter_emails?: boolean
}

const EmailPreferenceCheck = () => (
  <Typography variant="h6" color="secondary" display="inline" style={{ lineHeight: 'unset' }}>
    <CheckIcon style={{ marginRight: 20, width: 16 }} />
  </Typography>
)

const EmailPreferenceDash = () => (
  <Typography variant="h6" color="textSecondary" display="inline" style={{ lineHeight: 'unset' }}>
    <MinusIcon style={{ marginRight: 20, width: 16 }} />
  </Typography>
)

export const EmailPreferenceLine: React.FC<{ text: string; check?: boolean; first?: boolean }> = ({
  text,
  check,
  first,
}) => {
  return (
    <Box ml={1.0} mt={first ? 0 : 2} display="flex" alignItems="center">
      {check ? <EmailPreferenceCheck /> : <EmailPreferenceDash />}
      {text}
    </Box>
  )
}

export const EmailPreferenceNotes: React.FC<{ prefs: EmailPrefs }> = ({ prefs }) => {
  if (prefs.opt_in_general_marketing_emails && prefs.opt_in_newsletter_emails && prefs.opt_in_opportunity_emails) {
    return <EmailPreferenceLine check={true} first text="Subscribed to all" />
  } else if (
    !prefs.opt_in_general_marketing_emails &&
    !prefs.opt_in_newsletter_emails &&
    !prefs.opt_in_opportunity_emails
  ) {
    return <EmailPreferenceLine check={false} first text="Unsubscribed from all" />
  }
  return (
    <Fragment>
      <EmailPreferenceLine check={prefs.opt_in_opportunity_emails} first text="All Investment and Sell Opportunities" />
      <EmailPreferenceLine check={prefs.opt_in_newsletter_emails} text="Forge Newsletter" />
      <EmailPreferenceLine check={prefs.opt_in_general_marketing_emails} text="General Marketing Emails" />
    </Fragment>
  )
}

const getLogoIcon = (channel: string) => {
  switch (channel) {
    case 'email':
      return <EnvelopeIcon />
    case 'phone':
      return <PhoneIcon />
    case 'whatsapp':
      return <WhatsappIcon />
    case 'wechat':
      return <CommentsIcon />
    case 'linkedin':
      return <LinkedinIcon />
    default:
      return <CommentsIcon />
  }
}

const ContactMethodToInfoCard: React.FC<{
  method: ContactMethodFragment
  account: AccountWithRelationshipsAndContactMethodsFragment | undefined
}> = ({ method, account }) => {
  const [showModal, hideModal] = useModal(() => {
    return <AccountContactInformationModal hideModal={hideModal} accountId={method.account_id} contactMethod={method} />
  }, [method])
  const [mutate] = useDeleteContactMethodMutation({
    variables: {
      id: method.id,
    },
    refetchQueries: [{ query: AccountContactMethodsDocument, variables: { id: method.account_id } }],
  })
  const actions = (
    <Fragment>
      <PopoverListItem button onClick={showModal}>
        <PopoverListItemIcon>
          <PencilAltIcon />
        </PopoverListItemIcon>
        <PopoverListItemText>Edit</PopoverListItemText>
      </PopoverListItem>
      <PopoverListItem button onClick={() => mutate()}>
        <PopoverListItemIcon>
          <TrashIcon />
        </PopoverListItemIcon>
        <PopoverListItemText>Delete</PopoverListItemText>
      </PopoverListItem>
    </Fragment>
  )
  const props = {
    logo: <GreyIconWrapper>{getLogoIcon(method.channel)}</GreyIconWrapper>,
    leftTitle: method.formatted_channel_value || method.channel_value || '',
    leftSubtitle: `${method.is_primary ? 'Primary ' : ''} ${capitalize(method.channel)}`,
    notes: method.email_preferences && <EmailPreferenceNotes prefs={method.email_preferences} />,
    highlight: method.is_primary,
    action: <PopoverAction icon={<EllipsisVIcon />} actionMenu={actions} />,
  } as InfoCardProps

  if (method.channel === ContactMethodChannel.Linkedin) {
    return (
      <Link href={createValidAbsoluteLink(method.channel_value ?? '')} target="_blank" rel="noopener noreferrer">
        <SingleInfoCard
          {...props}
          leftTitle={`${account?.name ?? 'Account'}'s LinkedIn Account`}
          leftSubtitle={method.formatted_channel_value ?? ''}
        />
      </Link>
    )
  }

  return <SingleInfoCard {...props} />
}

const useStyles = makeStyles(({ spacing }: Theme) => ({
  sectionWrapper: {
    '&+&': {
      marginTop: spacing(2),
    },
  },
}))

const ContactInfoSection: React.FC<{ heading: string }> = ({ heading, children }) => {
  const classes = useStyles()
  return (
    <div className={classes.sectionWrapper}>
      <Box mb={1}>
        <Typography variant="subtitle2" fontWeight="bold">
          {heading}
        </Typography>
      </Box>
      {children}
    </div>
  )
}

export const AccountContactInformation: React.FC<{ accountId: string }> = ({ accountId }) => {
  const { data } = useAccountContactMethodsQuery({ variables: { id: accountId } })
  const contactMethods = data?.account?.contact_methods ?? []
  const account = data?.account
  const sortedContactMethods = contactMethods.sort((a, b) => {
    if ((a.is_primary && b.is_primary) || (!a.is_primary && !b.is_primary)) {
      return 0
    } else if (a.is_primary) {
      return -1
    }
    return 1
  })
  const [showModal, hideModal] = useModal(() => {
    return <AccountContactInformationModal hideModal={hideModal} accountId={accountId} />
  }, [accountId])
  const emailMethods = sortedContactMethods.filter(m => m.channel === 'email')
  const phoneMethods = sortedContactMethods.filter(m => m.channel === 'phone')
  const otherMethods = sortedContactMethods.filter(m => m.channel !== 'phone' && m.channel !== 'email')
  const emails = emailMethods.map(m => <ContactMethodToInfoCard method={m} account={data?.account} key={m.id} />)
  const phones = phoneMethods.map(m => <ContactMethodToInfoCard method={m} account={data?.account} key={m.id} />)
  const others = otherMethods.map(m => <ContactMethodToInfoCard method={m} account={data?.account} key={m.id} />)
  const relationships = account?.relationships ?? []
  const intermediary = account?.intermediary
  const pointsOfContact = relationships.filter(r => r.is_point_of_contact)
  const pointOfContactSection = account
    ? pointsOfContact.map(child => (
        <ChildAccountCard
          child={child}
          parentName={account.name}
          parentType={account.type}
          parentId={accountId}
          key={child.child_account.id}
          isIntermediary={intermediary?.id === child.id}
          supressHighlight={true}
        />
      ))
    : []
  let relationCard =
    intermediary &&
    account &&
    (intermediary!.parent_account.id === account!.id ? (
      <ChildAccountCard
        child={intermediary!}
        parentName={account!.name}
        parentType={account!.type}
        parentId={account!.id}
        key={intermediary!.id}
        isIntermediary={true}
        supressHighlight={true}
      />
    ) : (
      <ParentAccountCard
        parent={intermediary!}
        childName={account!.name}
        childId={account!.id}
        childType={account!.type}
        key={intermediary!.id}
        parentBrokerOrg={intermediary?.introduction?.parent_broker_account?.id}
        childBrokerOrg={intermediary?.introduction?.child_broker_account?.id}
        isIntermediary={true}
        supressHighlight={true}
      />
    ))
  return (
    <Card>
      <CardHeader title="Contact Information" />
      <CardContent>
        {relationCard && <ContactInfoSection heading="Intermediary">{relationCard}</ContactInfoSection>}
        {!!pointOfContactSection.length && (
          <ContactInfoSection heading="Points of Contact">{pointOfContactSection}</ContactInfoSection>
        )}
        {!!emails.length && <ContactInfoSection heading="Email">{emails}</ContactInfoSection>}
        {!!phones.length && <ContactInfoSection heading="Phone">{phones}</ContactInfoSection>}
        {!!others.length && <ContactInfoSection heading="Other">{others}</ContactInfoSection>}
        <Box mt={2}>
          <Button size="large" onClick={showModal} fullWidth>
            Add contact method
          </Button>
        </Box>
      </CardContent>
    </Card>
  )
}
